import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import userManager from "../authorization/authService";
import { AuthenticationState } from "./AuthenticationState";
import { RootState } from "../../app/store";
import { UserAuth } from "./UserAuth";
import { serializeOidcUser } from "./serializeUser";
import {
  registerUserAsync,
  loginUserAsync,
  redeemSignInTokenAsync,
  updateProfileNameAsync,
  verifySignInCodeAsync,
} from "./authenticationApi";
import { UserLoginResponse } from "./UserLoginResponse";
import { getUserInfoFromToken } from "./JwtDecode";
import { clearAllCookies } from "../extensions/clearAllCookies";
import { roleContainsAdmin } from "./roleContainsAdmin";
import { OrganizationRole } from "../instructors/enums/OrganizationRole";
import { storeInstructorAccessToken } from "./instructorToken";
import { signOutEverywhere } from "./signOutEverywhere";
const initialState: AuthenticationState = {
  initializationStatus: "not-initialized",
  userAuth: {
    id_token: "",
    session_state: "",
    access_token: "",
    token_type: "",
    scope: "",
    refresh_token: "",
    profile: {
      s_hash: "",
      sid: "",
      sub: "",
      auth_time: 0,
      idp: "",
      amr: [],
      emailaddress: "",
      name: "",
      role: "",
      picture: "",
      instructorId: "",
      organizationId: "",
      organizationRole: "",
    },
    expires_at: 0,
  },
  status: "idle",
  isAuthenticated: false,
};

export const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    updateAuthenticationState: (state, action) => {
      setUserAuthInfoByAuthManager(action.payload, state);
    },
    updateToken: (state, action: PayloadAction<string>) => {
      if (state.userAuth) {
        state.userAuth.access_token = action.payload;
      }
    },
    clearAuthenticationData: (state) => {
      // Explicitly remove access token from localStorage
      localStorage.removeItem("access_token");

      resetUserAuth(state);
      state.isAuthenticated = false;
      state.status = "idle";
      state.initializationStatus = "initialized";
    },
    setAuthenticationError: (state, _action) => {
      resetUserAuth(state);
      state.status = "failed";
      state.isAuthenticated = false;
      state.initializationStatus = "initialized";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateProfileNameAsync.fulfilled, (state, action) => {
        // Tokens carry the new name only after re-issue; keep the header live now.
        state.userAuth.profile.name = action.payload.name;
      })
      .addCase(loadInitialAuthenticationStateAsync.pending, (state) => {
        state.initializationStatus = "initializing";
        state.status = "loading";
      })
      .addCase(
        loadInitialAuthenticationStateAsync.fulfilled,
        (state, action) => {
          if (action.payload) {
            const isTokenAuth =
              !action.payload.id_token && action.payload.access_token;

            if (typeof isTokenAuth !== "undefined" && isTokenAuth) {
              state.userAuth.access_token = action.payload.access_token;

              if (action.payload.profile) {
                state.userAuth.profile.emailaddress =
                  action.payload.profile.emailaddress || "";
                state.userAuth.profile.role = action.payload.profile.role || "";
                state.userAuth.profile.sub = action.payload.profile.sub || "";
                state.userAuth.profile.name = action.payload.profile.name || "";
                state.userAuth.profile.instructorId =
                  action.payload.profile.instructorId || "";
                state.userAuth.profile.organizationId =
                  action.payload.profile.organizationId || "";
                state.userAuth.profile.organizationRole =
                  action.payload.profile.organizationRole || "";
              }

              state.isAuthenticated = true;
            } else {
              const decodedToken = getUserInfoFromToken(
                action.payload.access_token
              );
              state.userAuth.profile.emailaddress = decodedToken?.email || "";
              state.userAuth.profile.role = decodedToken?.role || "";
              state.userAuth.profile.name = decodedToken?.name || "";
              state.userAuth.profile.instructorId =
                decodedToken?.instructorId || "";
              state.userAuth.profile.organizationId =
                decodedToken?.organizationId || "";
              state.userAuth.profile.organizationRole =
                decodedToken?.organizationRole || "";

              setUserAuthInfoByAuthManager(action.payload, state);
            }

            state.initializationStatus = "initialized";
            state.status = "idle";
          } else {
            resetUserAuth(state);
            state.isAuthenticated = false;
            state.initializationStatus = "initialized";
            state.status = "idle";
          }
        }
      )
      .addCase(loadInitialAuthenticationStateAsync.rejected, (state) => {
        state.status = "failed";
        state.initializationStatus = "initialized";
      })
      .addCase(logoutAsync.pending, (state) => {
        state.status = "loading";
        state.initializationStatus = "initializing";
      })
      .addCase(logoutAsync.rejected, (state) => {
        state.status = "failed";
        state.isAuthenticated = false;
        state.initializationStatus = "initialized";
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        localStorage.removeItem("access_token");

        state.isAuthenticated = false;
        state.initializationStatus = "initialized";
        state.status = "idle";
      })
      .addCase(registerUserAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(registerUserAsync.fulfilled, (state, _action) => {
        state.status = "idle";
      })
      .addCase(registerUserAsync.rejected, (state, _action) => {
        state.status = "failed";
      })
      .addCase(loginUserAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(loginUserAsync.fulfilled, (state, action) => {
        applyLogin(state, action.payload);
      })
      .addCase(loginUserAsync.rejected, (state, _action) => {
        state.status = "failed";
      })
      // Passwordless logins (magic link / emailed code) land the same way as
      // a password login: same response shape, same stored token.
      .addCase(redeemSignInTokenAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(redeemSignInTokenAsync.fulfilled, (state, action) => {
        applyLogin(state, action.payload);
      })
      .addCase(redeemSignInTokenAsync.rejected, (state, _action) => {
        state.status = "failed";
      })
      .addCase(verifySignInCodeAsync.pending, (state) => {
        state.status = "loading";
      })
      .addCase(verifySignInCodeAsync.fulfilled, (state, action) => {
        applyLogin(state, action.payload);
      })
      .addCase(verifySignInCodeAsync.rejected, (state, _action) => {
        state.status = "failed";
      });
  },
});

function applyLogin(state: AuthenticationState, payload: UserLoginResponse) {
  const { token, userId, email } = payload;
  state.status = "idle";
  state.userAuth.access_token = token;
  state.userAuth.profile.sub = userId;
  state.userAuth.profile.emailaddress = email;
  state.isAuthenticated = true;

  const decodedToken = getUserInfoFromToken(token);
  if (decodedToken?.name) {
    state.userAuth.profile.name = decodedToken.name;
  }
  state.userAuth.profile.instructorId = decodedToken?.instructorId || "";
  state.userAuth.profile.organizationId = decodedToken?.organizationId || "";
  state.userAuth.profile.organizationRole = decodedToken?.organizationRole || "";

  storeInstructorAccessToken(token);
}

const resetUserAuth = (state: AuthenticationState) => {
  // Remove access token from localStorage
  localStorage.removeItem("access_token");

  state.userAuth = {
    id_token: "",
    session_state: "",
    access_token: "",
    token_type: "",
    scope: "",
    refresh_token: "",
    profile: {
      s_hash: "",
      sid: "",
      sub: "",
      auth_time: 0,
      idp: "",
      amr: [],
      role: "",
      emailaddress: "",
      name: "",
      picture: "",
      instructorId: "",
      organizationId: "",
      organizationRole: "",
    },
    expires_at: 0,
  };
};

const setUserAuthInfoByAuthManager = (
  authUser: UserAuth | null,
  state: AuthenticationState
) => {
  if (typeof authUser !== "undefined" && authUser !== null) {
    state.userAuth = {
      id_token: authUser.id_token || "",
      session_state: authUser.session_state || "",
      access_token: authUser.access_token || "",
      token_type: authUser.token_type || "",
      scope: authUser.scope || "",
      refresh_token: authUser.refresh_token || "",
      profile: {
        s_hash: authUser.profile?.s_hash || "",
        sid: authUser.profile?.sid || "",
        sub: authUser.profile?.sub || "",
        auth_time: authUser.profile?.auth_time || 0,
        idp: authUser.profile?.idp || "",
        amr: authUser.profile?.amr || [],
        role: authUser.profile?.role || "",
        emailaddress: authUser.profile?.emailaddress || "",
        name: authUser.profile?.name || "",
        picture: authUser.profile?.picture || "",
        instructorId: authUser.profile?.instructorId || "",
        organizationId: authUser.profile?.organizationId || "",
        organizationRole: authUser.profile?.organizationRole || "",
      },
      expires_at: authUser.expires_at || 0,
    };
    state.isAuthenticated = true;
    state.initializationStatus = "initialized";
    state.status = "idle";
  } else {
    state.isAuthenticated = false;
    state.initializationStatus = "initialized";
    state.status = "idle";
    state.userAuth = {
      id_token: "",
      session_state: "",
      access_token: "",
      token_type: "",
      scope: "",
      refresh_token: "",
      profile: {
        s_hash: "",
        sid: "",
        sub: "",
        auth_time: 0,
        idp: "",
        amr: [],
        role: "",
        emailaddress: "",
        name: "",
        picture: "",
        instructorId: "",
        organizationId: "",
        organizationRole: "",
      },
      expires_at: 0,
    };
  }
};

export const logoutAsync = createAsyncThunk(
  "authentication/logout",
  async (_, { dispatch, getState }) => {
    localStorage.setItem(
      "redirectUrl",
      window.location.pathname + window.location.search
    );

    const state = getState() as RootState;
    const hasOidcAuth = state.authentication.userAuth.id_token !== "";

    // Read before the sign-out removes it: the IdentityServer logout needs its id token.
    const user = hasOidcAuth ? await userManager.getUser() : null;
    await signOutEverywhere();

    clearAllCookies();

    if (hasOidcAuth) {
      await userManager.clearStaleState();

      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("oidc")) {
          localStorage.removeItem(key);
        }
      });

      if (user) {
        await userManager.signoutRedirect({
          id_token_hint: user.id_token,
          extraQueryParams: { prompt: "logout" },
        });
        await userManager.signoutRedirectCallback();
      }
    }

    dispatch(clearAuthenticationData());
  }
);

export const loadInitialAuthenticationStateAsync = createAsyncThunk(
  "authentication/loadInitialAuthenticationState",
  async () => {
    try {
      const storedToken = localStorage.getItem("access_token");

      if (storedToken) {
        const decodedToken = getUserInfoFromToken(storedToken);
        if (decodedToken) {
          return {
            access_token: storedToken,
            profile: {
              emailaddress: decodedToken.email || "",
              role: decodedToken.role || "",
              name: decodedToken.name || "",
              sub: `local-auth-${Date.now()}`,
              instructorId: decodedToken.instructorId || "",
              organizationId: decodedToken.organizationId || "",
              organizationRole: decodedToken.organizationRole || "",
            },
          } as UserAuth;
        }
      }

      const user = await userManager.getUser();
      if (typeof user !== "undefined" && user !== null) {
        const serializableUser = serializeOidcUser(user);
        return serializableUser;
      }

      return null;
    } catch (error) {
      console.error("Authentication initialization error:", error);
      return null;
    }
  }
);

export const selectAuthInitializationStatus = (state: RootState) =>
  state.authentication.initializationStatus;

export const selectIsAuthenticated = (state: RootState) =>
  state.authentication.isAuthenticated;

export const selectAuthenticatedRole = (state: RootState) =>
  state.authentication.userAuth.profile.role;

export const selectIsAdmin = (state: RootState) =>
  roleContainsAdmin(state.authentication.userAuth.profile.role);

export const selectUserProfile = (state: RootState) =>
  state.authentication.userAuth.profile;

export const selectInstructorId = (state: RootState) =>
  state.authentication.userAuth.profile.instructorId;

export const selectOrganizationId = (state: RootState) =>
  state.authentication.userAuth.profile.organizationId;

/**
 * The signed-in instructor's organization role. A token without the claim
 * (issued before organizations existed) belongs to a solo owner, so the
 * absent/unknown value falls back to Owner — mirroring the backend fallback.
 */
export const selectOrganizationRole = (state: RootState): OrganizationRole => {
  const raw = state.authentication.userAuth.profile.organizationRole;
  if (raw === OrganizationRole.Admin || raw === OrganizationRole.Instructor) {
    return raw;
  }
  return OrganizationRole.Owner;
};

export const selectIsOrgOwner = (state: RootState) =>
  selectOrganizationRole(state) === OrganizationRole.Owner;

export const selectIsOrgOwnerOrAdmin = (state: RootState) =>
  selectOrganizationRole(state) !== OrganizationRole.Instructor;

export const selectAuthMethod = (state: RootState) => {
  if (!state.authentication.isAuthenticated) return "none";
  return state.authentication.userAuth.id_token ? "oidc" : "token";
};

export const selectIsTokenExpired = (state: RootState) => {
  const expiresAt = state.authentication.userAuth.expires_at;
  const currentTime = Date.now() / 1000;
  return expiresAt && expiresAt < currentTime;
};

export const selectAuthenticationStatus = (state: RootState) =>
  state.authentication.status;

export const {
  clearAuthenticationData,
  updateAuthenticationState,
  setAuthenticationError,
  updateToken,
} = authenticationSlice.actions;

export default authenticationSlice.reducer;
